import { appSchema, Database, Model, Q, tableSchema } from '@nozbe/watermelondb';
import { FileStorage } from './abstract.file.system';
import SQLiteAdapter from '@nozbe/watermelondb/adapters/sqlite';
import { Platform } from 'react-native';
import LokiJSAdapter from '@nozbe/watermelondb/adapters/lokijs';
import { Directory, File, Paths, UploadType } from 'expo-file-system';
import { generateNameFromUrl } from '../utils';
import { ChatFileUploadHeadersDto, FileRecord, FileTypeEnum, isAudioSupportedBySafari, mediaInfoFrom } from '@matap/shared';
import { IoJob } from './io.job';
import { convertWebmToMp3 } from '../utils/media.utils';
import axios from 'axios';
import { initFactory } from '../utils/job.manager';
import { coreStore } from '@/redux';
import { text } from '@nozbe/watermelondb/decorators';

const worker = initFactory('web-file-io', { capacity: 1000, concurrency: 2, maxCapPolicy: 'drop' });
const TAG = '[unified-file-system]';

// Single database connection name
const DB_NAME = 'matap_shared_storage';

// Export table names so index.ts can use them
export const TEMP_TABLE = 'temp_db_records';
export const PERSIST_TABLE = 'persist_db_records';

const schema = appSchema({
  version: 2,
  tables: [
    tableSchema({
      name: TEMP_TABLE,
      columns: [
        { name: 'remoteUrl', isIndexed: true, isOptional: true, type: 'string' },
        { name: 'localAddress', isIndexed: true, isOptional: true, type: 'string' },
        { name: 'key', isIndexed: true, isOptional: true, type: 'string' },
      ],
    }),
    tableSchema({
      name: PERSIST_TABLE,
      columns: [
        { name: 'remoteUrl', isIndexed: true, isOptional: true, type: 'string' },
        { name: 'localAddress', isIndexed: true, isOptional: true, type: 'string' },
        { name: 'key', isIndexed: true, isOptional: true, type: 'string' },
      ],
    }),
  ],
});

class TempDBRecord extends Model {
  static table = TEMP_TABLE;

  @text('remoteUrl') remoteUrl!: string;
  @text('localAddress') localAddress!: string;
  @text('key') key!: string;
}

class PersistDBRecord extends Model {
  static table = PERSIST_TABLE;

  @text('remoteUrl') remoteUrl!: string;
  @text('localAddress') localAddress!: string;
  @text('key') key!: string;
}

// Global Singleton Setup
let sharedDatabase: Database;
let databaseInitializationPromise: Promise<Database> | null = null;

async function getSharedDatabase(): Promise<Database> {
  if (sharedDatabase) return sharedDatabase;
  if (databaseInitializationPromise) return databaseInitializationPromise;

  databaseInitializationPromise = new Promise<Database>((resolve, reject) => {
    const handleFatalError = (error: Error) => {
      console.error(TAG, 'Database failed to initialize:', error);
      reject(error);
    };

    const db = new Database({
      adapter:
        Platform.OS === 'web'
          ? new LokiJSAdapter({
              dbName: DB_NAME,
              schema,
              useWebWorker: false,
              useIncrementalIndexedDB: true,
              onSetUpError: handleFatalError,
              onQuotaExceededError: handleFatalError,
              _onFatalError: handleFatalError,
            })
          : new SQLiteAdapter({
              dbName: DB_NAME,
              schema,
              jsi: true,
              onSetUpError: handleFatalError,
            }),
      modelClasses: [TempDBRecord, PersistDBRecord],
    });

    db.get(TEMP_TABLE)
      .query()
      .fetchCount()
      .then(() => {
        sharedDatabase = db;
        resolve(db);
      })
      .catch(reject);
  });

  return databaseInitializationPromise;
}

export class UnifiedFileSystem extends FileStorage {
  private keyToAddress = new KeyToAddressMap();
  private webCache!: Cache;
  private baseDir?: Directory;
  private pendingPurge?: Promise<any>;
  private database?: Database;
  private collection?: any;
  private readonly tableName: string;

  constructor(name: string, tableName: string) {
    super(name);
    this.tableName = tableName;
    if (Platform.OS !== 'web') {
      this.baseDir = new Directory(Paths.cache, this.name);
    }
  }

  async initialize(): Promise<void> {
    this.database = await getSharedDatabase();
    this.collection = this.database.get(this.tableName);

    if (Platform.OS === 'web') {
      if (!('caches' in window)) {
        throw new Error('Unable to get web cache');
      }
      this.webCache = await caches.open(this.name);
    } else {
      if (!this.baseDir!.exists) {
        this.baseDir!.create();
      }
    }
  }

  private async findOne(where: any): Promise<TempDBRecord | PersistDBRecord | undefined> {
    return this.collection
      .query(where)
      .fetch()
      .then((res: string | any[]) => res[0]);
  }

  private async findAll(where: any): Promise<(TempDBRecord | PersistDBRecord)[]> {
    return this.collection.query(where).fetch();
  }

  private createOne(record: Partial<TempDBRecord | PersistDBRecord>) {
    return this.database!.write(() => {
      return this.collection.create((res: any) => {
        res.localAddress = Platform.OS === 'web' ? '' : record.localAddress;
        res.remoteUrl = record.remoteUrl;
        res.key = record.key;
      });
    });
  }

  public generateKey(remoteUrl: string) {
    return btoa(encodeURIComponent(remoteUrl));
  }

  public async getByUrl(url: string) {
    const key = this.generateKey(url);
    if (this.keyToAddress.has(key)) {
      return this.keyToAddress.get(key);
    }
    const record = await this.findOne(Q.where('remoteUrl', url));
    if (!record) {
      return undefined;
    }

    if (Platform.OS === 'web') {
      const cache = await this.webCache.match(record.remoteUrl);
      if (!cache) {
        await this.database!.write(() => record.destroyPermanently());
        return undefined;
      }
      const blob = await cache.blob();
      this.keyToAddress.set(key, URL.createObjectURL(blob));
    } else {
      const file = new File(record.localAddress);
      if (!file.exists) {
        await this.database!.write(() => record.destroyPermanently());
        return undefined;
      }
      this.keyToAddress.set(key, file.uri);
    }

    return this.keyToAddress.get(key);
  }

  async mapToUrl(localAddress: string, url: string): Promise<void> {
    const previousAddress = this.keyToAddress.get(this.generateKey(url));
    if (previousAddress === localAddress) {
      return;
    }

    const newKey = this.generateKey(url);
    const previousKey = this.keyToAddress.keyOfAddress(localAddress);
    if (previousKey === newKey) {
      return;
    }

    await this.findAll(Q.or(Q.where('localAddress', localAddress), Q.where('remoteUrl', url))).then(async (res) => {
      await this.database!.write(async () => {
        for (const record of res) {
          await record.destroyPermanently();
        }
      });
    });

    if (Platform.OS === 'web') {
      const blob = await fetch(localAddress).then((res) => res.blob());
      await this.webCache.put(
        url,
        new Response(blob, {
          status: 200,
          headers: {
            'Content-Type': blob.type,
            'Content-Length': blob.size.toString(),
          },
        }),
      );
    }

    await this.createOne({
      key: newKey,
      localAddress,
      remoteUrl: url,
    });
    this.keyToAddress.set(newKey, localAddress);

    if (previousAddress) {
      const uploadTask = this.activeUploads.get(previousAddress);
      uploadTask?.cancel();
      this.emit('moved', previousAddress, localAddress);
      this.released(previousAddress, `mapped to a new url ${previousAddress} => ${localAddress}`);
    }
  }

  public async upload(localAddress: string, url: string, mime: string, fileName: string, headers?: ChatFileUploadHeadersDto): Promise<FileRecord> {
    if (this.activeUploads.has(localAddress)) {
      return this.activeUploads.get(localAddress)!.promise;
    }

    this.setUploadProgress(localAddress, 0);

    const job = new IoJob<FileRecord>({
      worker,
      onRun: async (listener, activeTask) => {
        const key = this.generateKey(url);
        let fileRecord: FileRecord;

        if (Platform.OS === 'web') {
          const controller = new AbortController();
          listener.cancelListener = () => controller.abort();
          let blob: Blob = await fetch(localAddress).then((res) => res.blob());
          if (mime && blob.type !== mime) {
            blob = blob.slice(0, blob.size, mime);
          }

          if (headers?.type === FileTypeEnum.AUDIO && !isAudioSupportedBySafari(mime)) {
            console.log(TAG, 'converting to mp3');
            const start = performance.now();
            blob = await convertWebmToMp3(blob).catch((e) => blob);
            console.log(TAG, 'conversion took', performance.now() - start);
            const info = await mediaInfoFrom(blob);
            mime = info.mime;
            fileName = info.name;
            const uri = URL.createObjectURL(blob);

            this.setUploadProgress(uri, 0);

            if (this.keyToAddress.has(key)) {
              this.keyToAddress.set(key, uri);
            }
            this.activeUploads.delete(localAddress);
            this.activeUploads.set(uri, activeTask);
            this.emit('moved', localAddress, uri);
            this.released(localAddress, 'the local address changed due to audio conversion');
            localAddress = uri;
          }

          if (controller.signal.aborted) {
            throw new Error('cancelled');
          }

          const formData = new FormData();
          formData.append('file', blob, fileName);

          const response = await axios.post(url, formData, {
            headers: { ...headers },
            signal: controller.signal,
            onUploadProgress: (event) => this.setUploadProgress(localAddress, event.total ? Math.round((event.loaded / event.total) * 100) : 0),
          });

          fileRecord = response.data;
        } else {
          const file = new File(localAddress);
          const uploadTask = file.createUploadTask(`${axios.defaults.baseURL}/${url}`, {
            httpMethod: 'POST',
            uploadType: UploadType.MULTIPART,
            fieldName: 'file',
            mimeType: mime,
            headers: { Cookie: coreStore.getState().userReducer.cookies, ...headers },
            onProgress: (progress) => this.setUploadProgress(localAddress, Math.round((progress.bytesSent / progress.totalBytes) * 100)),
          });

          listener.cancelListener = () => uploadTask.cancel();

          const result = await uploadTask.uploadAsync();
          if (!result || result.status < 200 || result.status >= 300) {
            throw new Error(`Upload failed with status ${result?.status}`);
          }

          fileRecord = JSON.parse(result.body);
        }

        await this.mapToUrl(localAddress, fileRecord.metadata.url);

        this.setUploadProgress(localAddress, 100, fileRecord);

        return fileRecord;
      },
      onError: (error) => this.setUploadProgress(localAddress, 0, undefined, error),
      onEnd: () => {
        this.upProgress.delete(localAddress);
        this.activeUploads.delete(localAddress);
        this.printDiagnosticsReport('upload finalized');
      },
    });

    const task = job.run();
    this.activeUploads.set(localAddress, task);
    this.printDiagnosticsReport('new upload');

    return task.promise;
  }

  public async download(url: string): Promise<string> {
    if (this.activeDownloads.has(url)) {
      return this.activeDownloads.get(url)!.promise;
    }
    console.log('DOWNLOADING', url);
    this.setDownloadProgress(url, 0);

    const job = new IoJob<string>({
      worker,
      onRun: async (listener) => {
        let localAddress: string;

        if (Platform.OS === 'web') {
          const controller = new AbortController();
          listener.cancelListener = () => controller.abort();
          const response = await axios.get<Blob>(url, {
            responseType: 'blob',
            signal: controller.signal,
            onDownloadProgress: (event) => this.setDownloadProgress(url, event.total ? Math.round((event.loaded / event.total) * 100) : 0),
          });
          localAddress = URL.createObjectURL(response.data);
        } else {
          const file = new File(this.baseDir!, generateNameFromUrl(url));
          if (file.exists) file.delete();

          const downloadTask = File.createDownloadTask(url, file, {
            headers: { Cookie: coreStore.getState().userReducer.cookies },
            onProgress: (event) => this.setDownloadProgress(url, Math.round((event.bytesWritten / event.totalBytes) * 100)),
          });

          listener.cancelListener = () => downloadTask.cancel();

          await downloadTask.downloadAsync();
          localAddress = file.uri;
        }

        await this.mapToUrl(localAddress, url);

        this.setDownloadProgress(url, 100, localAddress);

        return localAddress;
      },
      onError: (error) => this.setDownloadProgress(url, 0, undefined, error),
      onEnd: () => {
        this.dlProgress.delete(url);
        this.activeDownloads.delete(url);
        this.printDiagnosticsReport('download finalized');
      },
    });

    const task = job.run();
    this.activeDownloads.set(url, task);
    this.printDiagnosticsReport('new download');

    return task.promise;
  }

  size(localAddress: string): number {
    return 0;
  }

  delete(localAddress: string) {}

  async purge(): Promise<void> {
    if (this.pendingPurge) {
      return this.pendingPurge;
    }
    this.pendingPurge = new Promise(async (resolve) => {
      console.log(TAG, `purging ${this.name}`);
      try {
        this.activeDownloads.forEach((task) => {
          task.cancel();
        });
        this.activeUploads.forEach((task) => {
          task.cancel();
        });

        this.emit('evicted', 'all', 'file system purged');

        if (Platform.OS === 'web') {
          await caches.delete(this.name);
          console.log(TAG, 'cleared web cache');
        }

        await this.database!.write(async () => {
          await this.collection.query().destroyAllPermanently();
        }).catch(console.error);

        console.log(TAG, 'cleared database records');

        if (Platform.OS !== 'web' && this.baseDir!.exists) {
          this.baseDir!.delete();
          this.baseDir = new Directory(Paths.cache, this.name);
        }

        this.keyToAddress.clear();

        if (Platform.OS === 'web') {
          this.webCache = await caches.open(this.name);
        } else {
          if (!this.baseDir!.exists) this.baseDir!.create();
        }

        console.log(TAG, 're initialized');

        this.printDiagnosticsReport('after purge()');
      } catch (e) {
        console.error(e);
      } finally {
        resolve(undefined);
      }
    }).finally(() => {
      this.pendingPurge = undefined;
    });

    return this.pendingPurge;
  }

  released(localAddress: string, reason: string) {
    if (!localAddress) {
      throw new Error('Unable to release when localAddress is not defined');
    }

    if (!this.activeUploads.get(localAddress)) {
      if (this.keyToAddress.keyOfAddress(localAddress)) {
        this.keyToAddress.delete(this.keyToAddress.keyOfAddress(localAddress)!);
      }
      if (Platform.OS === 'web') {
        URL.revokeObjectURL(localAddress);
      }
      this.emit('evicted', localAddress, reason);
    }
  }
}

class KeyToAddressMap extends Map<string, string> {
  private addressToKey = new Map<string, string>();

  set(key: string, value: string): this {
    const old = this.get(key);
    if (old) {
      this.addressToKey.delete(old);
    }
    this.addressToKey.set(value, key);
    return super.set(key, value);
  }

  keyOfAddress(address: string) {
    return this.addressToKey.get(address);
  }

  addressKeys() {
    return this.addressToKey.keys();
  }

  delete(key: string): boolean {
    const value = this.get(key);
    if (value) {
      this.addressToKey.delete(value);
    }
    return super.delete(key);
  }

  clear() {
    this.addressToKey.clear();
    super.clear();
  }
}
